
import React, { useEffect, useState, forwardRef } from "react";
import '../../components/DashboardSeller/PendingQuotesDetails.css';
import { getPreviewBuyerEnquiry, postSellerQuotation, deleteEnquiryOrItem, deleteEnquiryItem } from "../../API/Api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { getAuthToken } from "../../utils/auth";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import QuotationShared from "../../components/DashboardSeller/QuotationShared";
import { IoMdArrowRoundBack } from "react-icons/io";
import { FaTrash } from "react-icons/fa";

const PendingQuotesDetailsBuyer = ({ onBack, enquiryId }) => {
	const [enquiryData, setEnquiryData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [showSharedQuotation, setShowSharedQuotation] = useState(false);
	const [quotationResponse, setQuotationResponse] = useState(null);
	const navigate = useNavigate();
	const [errors, setErrors] = useState({});

	const [formData, setFormData] = useState({
		offerSuffix: '',
		offerDate: new Date().toISOString().split('T')[0],
		validityDate: '',
		paymentTerms: '',
		customPaymentTerms: '',
		gstType: '18',
		gstCustom: '',
		additionalNotes: '',
		terms: 'This quotation is valid for the next 2 days only. Pricing may change based on real-time market updates. Delivery time is approximate and subject to stock availability.',
		itemPrices: [] // Ensure this is always an array
	});

	const [charges, setCharges] = useState([{ amount: 0, description: "" }]);

	const [calculations, setCalculations] = useState({
		subtotal: 0,
		subtotalWithCharges: 0,
		gstAmount: 0,
		grandTotal: 0,
		itemTotals: []
	});

	const handleChargeChange = (index, field, value) => {
		const updatedCharges = [...charges];
		updatedCharges[index] = {
			...updatedCharges[index],
			[field]: field === "amount" ? parseFloat(value) || 0 : value
		};
		setCharges(updatedCharges);

		// Recalculate totals with the updated charges
		const newCalculations = calculateTotals(formData, updatedCharges);
		setCalculations(newCalculations);
	};

	const addChargeField = () => {
		setCharges([...charges, { amount: 0, description: "" }]);
	};

	const removeChargeField = (index) => {
		if (charges.length > 1) {
			const updatedCharges = [...charges];
			updatedCharges.splice(index, 1);
			setCharges(updatedCharges);

			const newCalculations = calculateTotals(formData, updatedCharges);
			setCalculations(newCalculations);
		}
	};

	const today = new Date();

	// Fetch enquiry data
	useEffect(() => {
		const fetchEnquiryData = async () => {
			try {
				const token = getAuthToken();
				const response = await getPreviewBuyerEnquiry(enquiryId, token);
				console.log("RESPONSE:::", response)
				setEnquiryData(response.data);
				setLoading(false);
			} catch (err) {
				setError(err.message || "Failed to fetch enquiry data");
				setLoading(false);
			}
		};


		fetchEnquiryData();
	}, [enquiryId]);

	// Format currency
	const formatCurrency = (amount) => {
		return new Intl.NumberFormat('en-IN', {
			style: 'currency',
			currency: 'INR',
			minimumFractionDigits: 2
		}).format(amount);
	};

	// Calculate item total
	const calculateItemTotal = (price, unit, quantity, quantityUnit) => {
		const numericPrice = parseFloat(price) || 0;
		const numericQuantity = parseFloat(quantity) || 0;

		if (unit.includes(quantityUnit.toLowerCase()) ||
			(unit === 'per pc' && quantityUnit === 'Pc') ||
			(unit === 'per meter' && quantityUnit.toLowerCase() === 'meters')) {
			return numericPrice * numericQuantity;
		}

		if (unit === 'per MT' && quantityUnit.toLowerCase() === 'kg') {
			return numericPrice * (numericQuantity / 1000);
		}

		if (unit === 'per kg' && quantityUnit === 'MT') {
			return numericPrice * (numericQuantity * 1000);
		}

		return numericPrice * numericQuantity;
	};

	useEffect(() => {
		if (enquiryData) {
			const initialItemPrices = enquiryData.enquiry_items.map(item => {
				const latestPrice = item.latest_quotation?.price || '';
				const latestUnit = item.latest_quotation?.unit ||
					(item.quantity_unit === 'Pc' ? 'per pc' : 'per kg');

				return {
					price: latestPrice,
					priceUnit: latestUnit
				};
			});

			setFormData(prev => ({
				...prev,
				itemPrices: initialItemPrices
			}));

			const validityDate = new Date();
			validityDate.setDate(validityDate.getDate() + 2);

			const latestQuotation = enquiryData.latest_quotation;

			// Safely initialize charges
			const initialCharges = latestQuotation?.other_charges && Array.isArray(latestQuotation.other_charges)
				? latestQuotation.other_charges.map(charge => ({
					amount: charge.amount || 0,
					description: charge.description || ''
				}))
				: [{ amount: 0, description: "" }];

			setCharges(initialCharges);

			const newFormData = {
				offerSuffix: '',
				offerDate: new Date().toISOString().split('T')[0],
				validityDate: validityDate.toISOString().split('T')[0],
				paymentTerms: enquiryData.payment_term || '50% advance, 50% on delivery',
				itemPrices: initialItemPrices,
				gstType: latestQuotation?.gst_percentage || '18',
				gstCustom: '',
				additionalNotes: latestQuotation?.notes || '',
				terms: latestQuotation?.term_conditions || 'This quotation is valid for the next 2 days only. Pricing may change based on real-time market updates. Delivery time is approximate and subject to stock availability.'
			};

			setFormData(newFormData);
			calculateTotals(newFormData, initialCharges);
		}
	}, [enquiryData]);

	const calculateTotals = (formData, chargesArray) => {
		if (!enquiryData || !formData.itemPrices) return {
			subtotal: 0,
			subtotalWithCharges: 0,
			gstAmount: 0,
			grandTotal: 0,
			itemTotals: []
		};

		let subtotal = 0;
		const itemTotals = [];

		enquiryData.enquiry_items.forEach((item, index) => {
			const price = (formData.itemPrices[index] && formData.itemPrices[index].price) || '';
			const unit = (formData.itemPrices[index] && formData.itemPrices[index].priceUnit) || '';

			const itemTotal = price && unit ?
				calculateItemTotal(price, unit, item.universal_qty, item.quantity_unit) : 0;

			subtotal += itemTotal;
			itemTotals.push(itemTotal);
		});

		// Calculate total other charges from the charges array
		const totalOtherCharges = chargesArray.reduce(
			(sum, charge) => sum + (parseFloat(charge.amount) || 0),
			0
		);

		const subtotalWithCharges = subtotal + totalOtherCharges;

		let gstPercent;
		if (formData.gstType === 'custom') {
			gstPercent = parseFloat(formData.gstCustom) || 0;
		} else {
			gstPercent = parseFloat(formData.gstType) || 0;
		}

		const gstAmount = subtotalWithCharges * (gstPercent / 100);
		const grandTotal = subtotalWithCharges + gstAmount;

		return {
			subtotal,
			subtotalWithCharges,
			gstAmount,
			grandTotal,
			itemTotals
		};
	};

	// Handle form changes
	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => {
			const newFormData = { ...prev, [name]: value };
			const newCalculations = calculateTotals(newFormData, charges);
			setCalculations(newCalculations);
			return newFormData;
		});
	};

	const handleItemChange = (index, field, value) => {
		setFormData(prev => {
			const newItemPrices = [...prev.itemPrices];
			if (!newItemPrices[index]) {
				newItemPrices[index] = { price: '', priceUnit: '' };
			}
			newItemPrices[index] = { ...newItemPrices[index], [field]: value };
			const newFormData = { ...prev, itemPrices: newItemPrices };
			const newCalculations = calculateTotals(newFormData, charges);
			setCalculations(newCalculations);
			return newFormData;
		});
	};

	// Handle date changes
	const handleDateChange = (name, date) => {
		setFormData(prev => ({
			...prev,
			[name]: date.toISOString().split('T')[0]
		}));
	};

	// Handle form submission
	const handleSubmit = async (e) => {
		e.preventDefault();

		const newErrors = {};
		if (!formData.offerSuffix?.trim()) {
			toast.error("Offer Number suffix is a required field.");
			return;
		}

		if (!formData.offerDate) {
			toast.error("Offer Date is a required field.");
			return;
		}

		if (!formData.validityDate) {
			toast.error("Valid Till date is a required field.");
			return;
		}

		if (Object.keys(newErrors).length > 0) {
			setErrors(newErrors);
			return;
		}

		const paymentTermsToSubmit = formData.paymentTerms === 'custom'
			? formData.customPaymentTerms
			: formData.paymentTerms;

		try {
			const finalQuote = enquiryData.enquiry_items.map((item, index) => ({
				...item,
				unitPrice: formData.itemPrices[index].price,
				priceUnit: formData.itemPrices[index].priceUnit,
				itemTotal: calculations.itemTotals[index],
			}));

			const apiPayload = {
				enquiry_id: enquiryId,
				offer_details: {
					offer_number: `QTN-${new Date().getFullYear()}-${formData.offerSuffix}`,
					offer_date: formData.offerDate,
					validity_date: formData.validityDate,
				},
				payment_terms: formData.paymentTerms,
				enquiry_item: finalQuote.map(item => ({
					enquiry_item_id: item.enquiry_item_id,
					product_id: item.product_id,
					price: item.unitPrice,
					unit: item.priceUnit,
					quantity: item.universal_qty,
					total_price: item.itemTotal
				})),
				pricing_summary: {
					subtotal: calculations.subtotal,
					other_charges: charges.filter(charge => charge.amount > 0 || charge.description !== ""),
					gst_details: {
						percentage: formData.gstType === 'custom' ? formData.gstCustom : formData.gstType,
						amount: calculations.gstAmount
					},
					grand_total: calculations.grandTotal
				},
				notes: formData.additionalNotes,
				gst: formData.gstType === 'custom' ? formData.gstCustom : formData.gstType,
				term_conditions: formData.terms,
				grand_total: calculations.grandTotal
			};

			const response = await postSellerQuotation(apiPayload);

			toast.success('Quotation submitted successfully!', {
				position: "top-right",
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});

			setQuotationResponse(response.data);
			setShowSharedQuotation(true);

		} catch (error) {
			console.error('Submission Error:', error);
			toast.error(error?.message || 'Failed to submit quotation', {
				position: "top-right",
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true,
				draggable: true,
				progress: undefined,
			});
		}
	};

	const CustomInput = forwardRef(({ value, onClick, placeholder }, ref) => (
		<input
			type="text"
			className="form-control"
			onClick={onClick}
			value={value}
			ref={ref}
			readOnly
			placeholder={placeholder}
		/>
	));

	const canDelete = enquiryData?.stelmart_status === "Pending";

	const handleDeleteEnquiry = async () => {
		if (!canDelete) return;

		if (window.confirm("Are you sure you want to delete this entire enquiry?")) {
			try {
				await deleteEnquiryOrItem(enquiryId);
				toast.success('Enquiry deleted successfully!');
				onBack();
			} catch (error) {
				console.error('Delete error:', error);
				toast.error(error.message || 'Failed to delete enquiry');
			}
		}
	};

	const handleDeleteItem = async (itemId) => {
		if (!canDelete) return;

		if (window.confirm("Are you sure you want to delete this item?")) {
			try {
				await deleteEnquiryOrItem(enquiryId, itemId);
				setEnquiryData(prev => ({
					...prev,
					enquiry_items: prev.enquiry_items.filter(item => item.enquiry_item_id !== itemId)
				}));
				toast.success('Item deleted successfully!');
			} catch (error) {
				console.error('Delete error:', error);
				toast.error(error.message || 'Failed to delete item');
			}
		}
	};


	if (loading) {
		return <div className="container">Loading...</div>;
	}

	if (error) {
		return <div className="container">Error: {error}</div>;
	}

	if (!enquiryData) {
		return <div className="container">No enquiry data found</div>;
	}

	return (
		<div className="container pending-quotes-details seller-quote-view">
			<ToastContainer />
			{showSharedQuotation ? (
				<QuotationShared
					onBack={() => setShowSharedQuotation(false)}
					quotationData={quotationResponse}
				/>
			) : (
				<main>
					<div className="card">
						<div className="card-header">
							<h2>Quotation Request for {enquiryData.enquiry_items[0]?.product_name || 'Product'}</h2>
							<div className="header-actions">
								{canDelete && (
									<button
										className="btn btn-danger"
										onClick={handleDeleteEnquiry}
									>
										<FaTrash /> Delete Entire Enquiry
									</button>
								)}
								<IoMdArrowRoundBack onClick={onBack} />
							</div>				</div>

						<div className="card-body">
							<div className="info-grid mb-6 seller-details-header">
								<div className="info-section">
									<h3 className="mb-4">Enquiry Details</h3>

									{enquiryData.enquiry_reference_number && (
										<div className="info-row">
											<span className="info-label">Enquiry Reference:</span>
											<span className="info-value">{enquiryData.enquiry_reference_number}</span>
										</div>
									)}

									{enquiryData.created_at && (
										<div className="info-row">
											<span className="info-label">Submitted On:</span>
											<span className="info-value">{new Date(enquiryData.created_at).toLocaleDateString()}</span>
										</div>
									)}

									{enquiryData.delivery_time && (
										<div className="info-row">
											<span className="info-label">Required Delivery:</span>
											<span className="info-value">
												{enquiryData.delivery_time}
												{enquiryData.delivery_time_other && ` (${enquiryData.delivery_time_other})`}
											</span>
										</div>
									)}

									{enquiryData.payment_term && (
										<div className="info-row">
											<span className="info-label">Payment Terms:</span>
											<span className="info-value">
												{enquiryData.payment_term}

												{enquiryData.payment_term_number_days && ` (${enquiryData.payment_term_number_days} days)`}
												{enquiryData.advance_type && (
													<>
														<br />
														<small>
															Advance Type: {enquiryData.advance_type} <br />
															Advance Payment Value — {enquiryData.advance_payment_value}
															{enquiryData.advance_type === 'Percentage' && '%'} <br />
															{enquiryData.rest_paid_within_days && (
																<>Rest payable within {enquiryData.rest_paid_within_days} days</>
															)}
														</small>
													</>
												)}



											</span>
										</div>
									)}

									{(enquiryData.enquiry_items?.[0]?.address || enquiryData.enquiry_items?.[0]?.pincode) && (
										<div className="info-row">
											<span className="info-label">Location:</span>
											<span className="info-value">
												{[enquiryData.enquiry_items[0]?.address, enquiryData.enquiry_items[0]?.pincode]
													.filter(Boolean)
													.join(', ')}
											</span>
										</div>
									)}
								</div>

								<div className="info-section">
									{/* Your quotation details section remains unchanged */}
									{/* You can apply the same conditional rendering pattern here if needed */}
								</div>
							</div>

							{/* <h3 className="mb-4">Items to Quote</h3>
              <form id="sellerQuoteForm" onSubmit={handleSubmit}>
                <div id="itemsToQuote">
                  {enquiryData.enquiry_items.map((item, index) => (
                    <div key={index} className="seller-item-card">
                      <h4>Item {index + 1}</h4>
                      <div className="item-properties">
                        <div className="item-property">
                          <strong>Product Name</strong>
                          <span>{item.product_name}</span>
                        </div>
                        <div className="item-property">
                          <strong>Size</strong>
                          <span>{item.size}</span>
                        </div>
                        <div className="item-property">
                          <strong>Wall Thickness</strong>
                          <span>{item.wall_thickness}</span>
                        </div>
                        <div className="item-property">
                          <strong>Finish</strong>
                          <span>{item.finish}</span>
                        </div>
                        <div className="item-property">
                          <strong>Specification</strong>
                          <span>{item.specification}</span>
                        </div>
                        <div className="item-property">
                          <strong>End</strong>
                          <span>{item.end}</span>
                        </div>
                        <div className="item-property">
                          <strong>Length</strong>
                          <span>{item.length}</span>
                        </div>
                        <div className="item-property">
                          <strong>Price Basis</strong>
                          <span>{item.price_basis}</span>
                        </div>
                        <div className="item-property">
                          <strong>Quantity</strong>
                          <span>{item.universal_qty} {item.quantity_unit}</span>
                        </div>
                      </div>

                      <div className="form-row mt-4">
                        <div className="form-col">
                          <label>Your Pricing</label>
                          <div className="pricing-section">
                            <div className="price-input-group">
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="price-input form-control"
                                placeholder="Enter price"
                                value={formData.itemPrices[index]?.price || ''}
                                onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                                required
                              />
                              <select
                                className="unit-select form-control"
                                value={formData.itemPrices[index]?.priceUnit || ''}
                                onChange={(e) => handleItemChange(index, 'priceUnit', e.target.value)}
                              >
                                <option value="per kg">Per Kilogram (₹/kg)</option>
                                <option value="per MT">Per Metric Ton (₹/MT)</option>
                                <option value="per pc">Per Piece (₹/pc)</option>
                                <option value="per meter">Per Meter (₹/m)</option>
                                <option value="per foot">Per Foot (₹/ft)</option>
                                <option value="per bundle">Per Bundle</option>
                              </select>
                            </div>
                            <div className="item-total">
                              {formatCurrency(calculations.itemTotals[index] || 0)}
                            </div>
                          </div>
                          <p className="rate-disclaimer text-muted">
                            Base quantity: {item.universal_qty} {item.quantity_unit}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <hr className="my-6" />

                <div className="summary-section">
                  <h3 className="mb-4">Quotation Summary</h3>

                  <div className="calculation-row">
                    <span>Subtotal:</span>
                    <span id="subtotal">{formatCurrency(calculations.subtotal)}</span>
                  </div>

                  <div className="form-group mt-4">
                    <label>Other Charges</label>
                    <div className="other-charges-container">
                      {charges.map((charge, index) => (
                        <div key={index} className="other-charges-input">
                          <input
                            min="0"
                            step="0.01"
                            placeholder="Amount"
                            className="form-control"
                            type="number"
                            value={charge.amount}
                            onChange={(e) => handleChargeChange(index, "amount", e.target.value)}
                          />
                          <input
                            placeholder="Description"
                            className="form-control othercharges_descr"
                            type="text"
                            value={charge.description}
                            onChange={(e) => handleChargeChange(index, "description", e.target.value)}
                          />
                          {charges.length > 1 && (
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() => removeChargeField(index)}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                      <button type="button" className="btn btn-secondary mt-2" onClick={addChargeField}>
                        + Add More Charges
                      </button>
                    </div>
                  </div>

                  <div className="calculation-row">
                    <span>Subtotal with Charges:</span>
                    <span id="subtotalWithCharges">{formatCurrency(calculations.subtotalWithCharges)}</span>
                  </div>

                  <div className="form-row mt-4">
                    <div className="form-col">
                      <label>GST</label>
                      <div className="gst-selector">
                        <select
                          id="gstType"
                          className="form-control"
                          name="gstType"
                          value={formData.gstType}
                          onChange={handleChange}
                        >
                          <option value="0">0% (Exempt)</option>
                          <option value="5">5%</option>
                          <option value="12">12%</option>
                          <option value="18">18%</option>
                          <option value="28">28%</option>
                          <option value="custom">Custom %</option>
                        </select>
                        {formData.gstType === 'custom' && (
                          <input
                            type="number"
                            id="gstCustom"
                            className="gst-custom-input form-control"
                            min="0"
                            max="100"
                            step="0.01"
                            placeholder="Enter %"
                            name="gstCustom"
                            value={formData.gstCustom}
                            onChange={handleChange}
                          />
                        )}
                      </div>
                    </div>
                    <div className="form-col">
                      <label>GST Amount</label>
                      <input
                        type="text"
                        id="gstAmount"
                        className="form-control"
                        value={formatCurrency(calculations.gstAmount)}
                        readOnly
                      />
                    </div>
                  </div>

                  <div className="calculation-row grand-total mt-4">
                    <span>GRAND TOTAL:</span>
                    <span id="grandTotal">{formatCurrency(calculations.grandTotal)}</span>
                  </div>
                </div>

                <div className="form-group mt-6">
                  <label htmlFor="additionalNotes">Additional Notes (Optional)</label>
                  <textarea
                    id="additionalNotes"
                    rows="2"
                    placeholder="e.g., bulk discounts, material certs..."
                    className="form-control"
                    name="additionalNotes"
                    value={formData.additionalNotes}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <div className="form-group mt-4">
                  <label htmlFor="terms">Terms & Conditions</label>
                  <textarea
                    id="terms"
                    rows="4"
                    className="form-control"
                    name="terms"
                    value={formData.terms}
                    onChange={handleChange}
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className={`btn btn-block mt-6 ${enquiryData.latest_quotation ? 'bg-gray-400 cursor-not-allowed' : 'btn-primary'}`}
                  disabled={!!enquiryData.latest_quotation}
                >
                  {enquiryData.latest_quotation
                    ? 'Already Submitted Quotation to Stelmart'
                    : 'Submit Quotation to Stelmart'}
                </button>
              </form> */}

							<table className="table seller-item-table">
								<thead>
									<tr>
										<th>#</th>
										<th>Product Name</th>
										<th>Brand Name</th>
										<th>Size</th>
										<th>Wall Thickness</th>
										<th>Finish</th>
										<th>Specification</th>
										<th>End</th>
										<th>Length</th>
										<th>Price Basis</th>
										<th>Quantity</th>
										<th>Action</th>
										{/* <th>Your Price</th> */}
										{/* <th>Unit</th> */}
										{/* <th>Item Total</th> */}
									</tr>
								</thead>
								<tbody>
									{enquiryData.enquiry_items.map((item, index) => (
										<tr key={item.enquiry_item_id || index}>
											<td>{index + 1}</td>
											<td>{item.product_name}</td>
											<td>
												<ul style={{ paddingLeft: '16px', margin: 0 }}>
													{item.brands.map((brand, index) => (
														<li key={index}>{brand.name}</li>
													))}
												</ul>

											</td>
											<td>{item.size}</td>
											<td>{item.wall_thickness}</td>
											<td>{item.finish}</td>
											<td>{item.specification}</td>
											<td>{item.end}</td>
											<td>{item.length}</td>
											<td>{item.price_basis}</td>
											<td>{item.universal_qty} {item.quantity_unit}</td>
											<td>
												{canDelete && (
													<button
														className="btn btn-sm btn-danger"
														onClick={() => handleDeleteItem(item.enquiry_item_id)}
														title="Delete this item"
													>
														<FaTrash />
													</button>
												)}
											</td>
											{/* <td>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          className="form-control"
                          value={formData.itemPrices[index]?.price || ''}
                          onChange={(e) => handleItemChange(index, 'price', e.target.value)}
                          placeholder="Enter price"
                          required
                        />
                      </td> */}
											{/* <td>
                        <select
                          className="form-control"
                          value={formData.itemPrices[index]?.priceUnit || ''}
                          onChange={(e) => handleItemChange(index, 'priceUnit', e.target.value)}
                        >
                          <option value="per kg">(₹/kg)</option>
                          <option value="per MT">(₹/MT)</option>
                          <option value="per pc">(₹/pc)</option>
                          <option value="per meter">(₹/m)</option>
                          <option value="per foot">(₹/ft)</option>
                          <option value="per bundle">Per Bundle</option>
                        </select>
                      </td>
                      <td>
                        ₹{calculations.itemTotals[index] ? calculations.itemTotals[index].toFixed(2) : '0.00'}
                      </td> */}
										</tr>
									))}
								</tbody>
							</table>
						</div>
					</div>
				</main>
			)}
		</div>
	);
};

export default PendingQuotesDetailsBuyer;